<template>
  <div class="question-editor-dispatcher">
    <div v-if="loadError" class="dispatch-state dispatch-error">
      <p>{{ loadError }}</p>
      <router-link to="/admin/questions" class="back-link">← Back to questions</router-link>
    </div>

    <div v-else-if="resolving" class="dispatch-state dispatch-loading">
      Loading question…
    </div>

    <component
      v-else
      :is="editorComponent"
    />
  </div>
</template>

<script>
// Router target for /admin/questions/new and /admin/questions/:id/edit.
//
// Resolves the question type, then mounts the matching editor component.
// Each editor (StepperEditor, SingleChoiceEditor, ...) handles its own
// data loading from this.$route — the dispatcher just decides which one
// to render.
//
//   create mode:  read ?type=<question_type> from the URL
//   edit mode:    look up the cached question by id; fall back to a fetch
//
// In edit mode, the editor will fetch the same row again when it mounts.
// That's intentional — the child editors are self-contained, and a second
// GET is cheap. Keeps the editors usable without a dispatcher (e.g. for
// hand-routing during dev).

import apiService from '@/services/apiService';
import { questionsState } from '../_data/questions.js';

import StepperEditor from './StepperEditor.vue';
import SingleChoiceEditor from './SingleChoiceEditor.vue';
import MatrixEditor from './MatrixEditor.vue';
import ShortTextEditor from './ShortTextEditor.vue';
import NumberEditor from './NumberEditor.vue';
import NumericGridEditor from './NumericGridEditor.vue';

const EDITORS = {
  numeric_stepper: StepperEditor,
  single_choice: SingleChoiceEditor,
  matrix: MatrixEditor,
  short_text: ShortTextEditor,
  number: NumberEditor,
  numeric_grid: NumericGridEditor,
};

export default {
  name: 'QuestionEditor',
  data() {
    return {
      resolvedType: null,
      resolving: true,
      loadError: null,
    };
  },
  computed: {
    isCreate() {
      return this.$route.name === 'QuestionEditorNew';
    },
    editorComponent() {
      return EDITORS[this.resolvedType] || null;
    },
  },
  watch: {
    '$route': {
      immediate: true,
      handler() {
        this.resolve();
      },
    },
  },
  methods: {
    async resolve() {
      this.loadError = null;
      this.resolving = true;
      try {
        if (this.isCreate) {
          // Create mode: type comes from ?type=. Default to numeric_stepper
          // for backwards-compatibility with old links.
          const type = this.$route.query.type || 'numeric_stepper';
          if (!EDITORS[type]) {
            this.loadError = `Unknown or unsupported question type: '${type}'.`;
            return;
          }
          this.resolvedType = type;
        } else {
          // Edit mode: try the in-memory cache first (the bank page
          // populates it on mount). On cache miss, fetch the question
          // just to learn its type.
          const id = Number(this.$route.params.id);
          const cached = questionsState.items.find(q => q.id === id);
          if (cached && cached.question_type) {
            this.resolvedType = cached.question_type;
          } else {
            const q = await apiService.fetchQuestion(id);
            this.resolvedType = q.question_type;
          }
          if (!EDITORS[this.resolvedType]) {
            this.loadError = `No editor available for question type '${this.resolvedType}'.`;
          }
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          this.loadError = `No question found with id ${this.$route.params.id}.`;
        } else {
          const detail = e?.response?.data?.detail;
          this.loadError = typeof detail === 'string'
            ? detail
            : 'Failed to load question.';
        }
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style scoped>
.dispatch-state {
  max-width: 1080px;
  margin: 0 auto;
  padding: 4rem 1.5rem;
  text-align: center;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
}

.dispatch-loading {
  color: #888780;
  font-size: 14px;
}

.dispatch-error {
  color: #5f5e5a;
}

.back-link {
  display: inline-block;
  margin-top: 1rem;
  color: #534AB7;
  text-decoration: none;
  font-size: 14px;
}

.back-link:hover {
  text-decoration: underline;
}
</style>
